// src/service/testimonial.service.js
import axios from 'axios';
import config from "../environment/config";
import { authHeader } from "./auth.header"; // ✅ ADDED: Import auth header

const API_BASE_URL = config.API_URL;
const TESTIMONIAL_API = `${API_BASE_URL}/api/testimonials`;

/**
 * Fetch all active testimonials for public display
 * No authentication required
 */
export const getActiveTestimonials = async () => {
  try {
    console.log('📡 Fetching active testimonials (public)...');
    const response = await axios.get(TESTIMONIAL_API);
    console.log('✅ Active testimonials received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching active testimonials:', error);
    throw error;
  }
};

/**
 * Fetch all testimonials including inactive (Admin use)
 * Requires authentication
 */
export const getAllTestimonials = async () => {
  try {
    console.log('📡 Fetching all testimonials (admin)...');
    const response = await axios.get(
      `${TESTIMONIAL_API}/all`,
      {
        headers: authHeader(), // ✅ ADDED: Auth header
        timeout: 15000
      }
    );
    console.log('✅ All testimonials received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching all testimonials:', error);
    throw error;
  }
};

/**
 * Fetch single testimonial by ID
 * No authentication required for public view
 */
export const getTestimonialById = async (id) => {
  try {
    console.log('📡 Fetching testimonial by ID:', id);
    const response = await axios.get(`${TESTIMONIAL_API}/${id}`);
    console.log('✅ Testimonial received:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ Error fetching testimonial ${id}:`, error);
    throw error;
  }
};

/**
 * Create new testimonial (Admin use)
 * Requires ADMIN authentication
 */
export const createTestimonial = async (testimonialData) => {
  try {
    console.log('📤 Creating testimonial:', testimonialData);
    
    const response = await axios.post(
      TESTIMONIAL_API,
      testimonialData,
      {
        headers: {
          ...authHeader(), // ✅ ADDED: Auth header with JWT token
          'Content-Type': 'application/json'
        },
        timeout: 10000
      }
    );
    
    console.log('✅ Testimonial created:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating testimonial:', error.response?.data || error);
    throw error;
  }
};

/**
 * Update existing testimonial (Admin use)
 * Requires ADMIN authentication
 */
export const updateTestimonial = async (id, testimonialData) => {
  try {
    console.log('🔄 Updating testimonial:', id, testimonialData);
    
    const response = await axios.put(
      `${TESTIMONIAL_API}/${id}`,
      testimonialData,
      {
        headers: {
          ...authHeader(), // ✅ ADDED: Auth header
          'Content-Type': 'application/json'
        },
        timeout: 10000
      }
    );
    
    console.log('✅ Testimonial updated:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ Error updating testimonial ${id}:`, error.response?.data || error);
    throw error;
  }
};

/**
 * Deactivate testimonial (soft delete) (Admin use)
 * Requires ADMIN authentication
 */
export const deactivateTestimonial = async (id) => {
  try {
    console.log('🔄 Deactivating testimonial:', id);
    
    const response = await axios.patch(
      `${TESTIMONIAL_API}/${id}/deactivate`,
      {},
      {
        headers: authHeader(), // ✅ ADDED: Auth header
        timeout: 10000
      }
    );
    
    console.log('✅ Testimonial deactivated:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ Error deactivating testimonial ${id}:`, error.response?.data || error);
    throw error;
  }
};

/**
 * Delete testimonial permanently (Admin use)
 * Requires ADMIN authentication
 */
export const deleteTestimonial = async (id) => {
  try {
    console.log('🗑️ Deleting testimonial:', id);
    
    const response = await axios.delete(
      `${TESTIMONIAL_API}/${id}`,
      {
        headers: authHeader(), // ✅ ADDED: Auth header
        timeout: 10000
      }
    );
    
    console.log('✅ Testimonial deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ Error deleting testimonial ${id}:`, error.response?.data || error);
    throw error;
  }
};

// ✅ Default export
const testimonialService = {
  getActiveTestimonials,
  getAllTestimonials,
  getTestimonialById,
  createTestimonial,
  updateTestimonial,
  deactivateTestimonial,
  deleteTestimonial
};

export default testimonialService;